import { Product } from '@pedix-workspace/utils';
import { ProductStockUpdateContext } from './get-product-stock-updates-for-products';
import { ProductStock, ProductStockEntityType } from './stock.types';
import { ProductStockWriteOp, resolveProductStockWrite } from './product-stock.model';

/**
 * Entrada flat para derivar los writes a `productStock`: una entidad stockeable + su stock DESEADO
 * (`null` = infinito/sin control → persiste el doc con `stockQty:null`, conservando su historial).
 * Mismo shape que `ProductStock` pero acotado a lo que define un write.
 */
export type ProductStockWriteEntry = Pick<ProductStock, 'id' | 'productId' | 'entityType'> & {
  stockQty: number | null;
};

export type GetProductStockOpsOptions = {
  /**
   * When `true`, infinite entities (`stockQty == null`) emit an `upsert` that persists the doc with
   * `stockQty: null` (baja de control, conserva el historial — e.g. an Excel import that turns a
   * previously-finite stock into infinite). When `false` (default, for fresh creation/backfill where
   * no doc exists yet), infinite entities are skipped: absence already means infinite, so no write is
   * needed.
   */
  persistInfinite?: boolean;
};

/**
 * Mapea entradas de stock flat a write ops de `productStock`: finito → `upsert`; infinito (`null`) →
 * `upsert` con `stockQty:null` si `persistInfinite` (baja de control, conserva historial), sino se
 * saltea (ausencia == infinito para entidades nunca controladas). Doc id = id de la entidad. Los
 * callers arman las entradas desde su fuente de stock (Excel, mapa de `productStock`, etc.).
 */
export function getProductStockWriteOps(
  entries: ProductStockWriteEntry[],
  context: ProductStockUpdateContext,
  options: GetProductStockOpsOptions = {},
): ProductStockWriteOp[] {
  return entries
    .map(entry => buildOp(entry, context, options))
    .filter((op): op is ProductStockWriteOp => op !== null);
}

/** Una entidad stockeable de un producto: su doc id en `productStock` + su tipo. */
export type StockableEntity = { id: string; entityType: ProductStockEntityType };

/**
 * Enumeración ÚNICA de las entidades stockeables de un producto (= docs en `productStock`): con
 * presentaciones → sus items; sino → el producto; MÁS los items de opciones EMBEBIDAS
 * (`options[].items[]`). Es la fuente de verdad compartida entre quien LEE/ESCRIBE el stock
 * (`getStockForProduct`) y quien lo RECONCILIA por diff (`getRemovedStockEntityIds`): enumerar acá
 * MENOS ids que los que la escritura crea haría que el diff borre stock válido. Las opciones
 * COMPARTIDAS no traen `options` embebidas → no aportan ids (no hace falta filtrar por `linkedOptionIds`).
 */
export function getStockableEntities(
  product: Pick<Product, 'id' | 'presentations' | 'options'>,
): StockableEntity[] {
  const embeddedOptionItems = (product.options ?? []).flatMap(option =>
    (option.items ?? []).map(
      (item): StockableEntity => ({ id: item.id, entityType: 'option-item' }),
    ),
  );

  const presentationItems = product.presentations?.items ?? [];
  const baseEntities: StockableEntity[] =
    presentationItems.length > 0
      ? presentationItems.map(
          (item): StockableEntity => ({ id: item.id, entityType: 'presentation' }),
        )
      : [{ id: product.id, entityType: 'product' }];

  return [...baseEntities, ...embeddedOptionItems];
}

/** Ids de las entidades stockeables (= doc ids en `productStock`). Lee SOLO ids (no `stockQty`). */
export function getProductStockEntityIds(
  product: Pick<Product, 'id' | 'presentations' | 'options'>,
): string[] {
  return getStockableEntities(product).map(entity => entity.id);
}

/**
 * Ids presentes en `before` que ya no están en `after` → sus docs de `productStock` quedaron
 * huérfanos (presentación/item removido, transición de nivel). Diff único que usan el trigger
 * `reconcile-product-stock` y el re-clone del catálogo.
 */
export function getRemovedStockEntityIds(
  before: Pick<Product, 'id' | 'presentations' | 'options'>,
  after: Pick<Product, 'id' | 'presentations' | 'options'>,
): string[] {
  const afterEntityIds = new Set(getProductStockEntityIds(after));
  return getProductStockEntityIds(before).filter(id => !afterEntityIds.has(id));
}

function buildOp(
  entry: ProductStockWriteEntry,
  context: ProductStockUpdateContext,
  options: GetProductStockOpsOptions,
): ProductStockWriteOp | null {
  const isFinite = entry.stockQty != null;

  if (!isFinite && !options.persistInfinite) {
    return null;
  }

  return {
    entityId: entry.id,
    write: resolveProductStockWrite({
      establishmentId: context.establishmentId,
      productId: entry.productId,
      entityType: entry.entityType,
      stockQty: isFinite ? entry.stockQty : null,
      source: context.source,
      uid: context.uid,
      reason: context.reason,
      systemReason: context.systemReason,
    }),
  };
}
